/**
 * Page Transition Overlay — Full-screen overlay fade-out on page load.
 *
 * Dependencies: none (vanilla JS, Web Animations API)
 * CSS dependency: tw-entry.css → tw-build.css (overlay styles, in @layer base)
 *
 * Lifecycle:
 *   RENDERING → page:loaded → LOADED → beforeunload → UNLOADING → pageshow → LOADED
 *
 * Adapted from: fullscreen-page-transition-overlay pattern
 * Source: Concept Shopify Theme by RoarTheme
 */

(function () {
	"use strict";

	// ── Configuration ──────────────────────────────────────────
	var OVERLAY_ID = "page-loading-overlay";
	var FADE_DURATION = 1000; // ms
	var FADE_EASING = "ease";
	var FALLBACK_TIMEOUT = 5000; // force-remove overlay after 10s if something goes wrong
	var OVERLAY_Z_INDEX = 50;

	// ── State ───────────────────────────────────────────────────
	var overlay = null;
	var isFadingOut = false;

	// ── Utility: Web Animations API with CSS transition fallback ──
	function fadeOutOverlay(callback) {
		if (!overlay || isFadingOut) return;
		isFadingOut = true;
		// Option A: Web Animations API (native, no dependencies)
		if (overlay.animate) {
			var animation = overlay.animate(
				{ opacity: [1, 0] },
				{
					duration: FADE_DURATION,
					easing: FADE_EASING,
					fill: "forwards",
				},
			);

			if (animation.finished) {
				animation.finished.then(function () {
					overlay.hidden = true;
					if (callback) callback();
				});
			} else {
				// `finished` might not be available in very old browsers
				overlay.addEventListener(
					"animationend",
					function () {
						overlay.hidden = true;
						if (callback) callback();
					},
					{ once: true },
				);
			}
		} else {
			// Option B: CSS transition fallback
			overlay.style.transition =
				"opacity " + FADE_DURATION + "ms " + FADE_EASING;
			overlay.style.opacity = "0";
			overlay.addEventListener(
				"transitionend",
				function () {
					overlay.hidden = true;
					if (callback) callback();
				},
				{ once: true },
			);
		}
	}

	// ── Content Visibility Gate ─────────────────────────────────
	function removeRenderingGate() {
		requestAnimationFrame(function () {
			requestAnimationFrame(function () {
				document.body.removeAttribute("data-page-rendering");
			});
		});
	}

	// ── Initialize overlay (must run after DOM parsed) ──────────
	function init() {
		overlay = document.getElementById(OVERLAY_ID);
		if (!overlay) return false;

		overlay.style.zIndex = String(OVERLAY_Z_INDEX);
		return true;
	}

	// ── DOM Ready handler ───────────────────────────────────────
	function onDOMReady() {
		// Init must happen here — DOM is fully parsed, overlay element exists
		if (!init()) return;

		// Fallback timeout: force-remove overlay if something errors
		var fallbackTimer = setTimeout(function () {
			if (overlay && !overlay.hidden) {
				console.warn(
					"[PageTransitionOverlay] Fallback timeout — force-removing overlay.",
				);
				fadeOutOverlay(function () {
					removeRenderingGate();
				});
			}
		}, FALLBACK_TIMEOUT);

		// Fade out overlay + remove rendering gate
		function onPageLoaded() {
			clearTimeout(fallbackTimer);
			fadeOutOverlay(function () {
				removeRenderingGate();
			});
		}

		// Listen for page:loaded (dispatched below)
		document.addEventListener("page:loaded", onPageLoaded);

		// Add .loaded class for any CSS hooks
		document.body.classList.add("loaded");

		// Dispatch the custom event — triggers overlay fade-out
		document.dispatchEvent(new CustomEvent("page:loaded"));
	}

	// ── Entry: wait for DOMContentLoaded ────────────────────────
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", onDOMReady);
	} else {
		// DOMContentLoaded already fired (script loaded late, e.g. Turbo navigation)
		onDOMReady();
	}

	// ── Navigation Away / bfcache hooks ─────────────────────────
	window.addEventListener("beforeunload", function () {
		document.body.classList.add("unloading");
	});

	window.addEventListener("pageshow", function (event) {
		// User returned via browser back/forward (bfcache restore)
		if (event.persisted) {
			document.body.classList.remove("unloading");
		}
	});

	// ── Public API (exposed for debugging / manual control) ──────
	if (typeof window !== "undefined") {
		window.PageTransitionOverlay = {
			fadeOut: fadeOutOverlay,
			removeGate: removeRenderingGate,
			getOverlay: function () {
				return overlay;
			},
		};
	}
})();
